import React, { useEffect, useState } from "react";
import {
  EyeIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import {
  addToWatchlist,
  listWatchlist,
  removeFromWatchlist,
} from "../api/watchlistApi";
import AskCouncilButton from "./AskCouncilButton";

/**
 * Watchlist card — shown on Holdings. Lets the user follow tickers they
 * don't own (research, post-trim follow-up, "thinking about buying"). Lens
 * is one click away per item via AskCouncil.
 */
const WatchlistCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ symbol: "", name: "" });
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const res = await listWatchlist();
      setItems(res.data?.watchlist || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!draft.symbol.trim()) return;
    setAdding(true);
    setError("");
    try {
      await addToWatchlist({
        symbol: draft.symbol.trim().toUpperCase(),
        name: draft.name.trim(),
      });
      setDraft({ symbol: "", name: "" });
      await refresh();
    } catch (err) {
      setError(
        err?.response?.data?.symbol?.[0] ||
          err?.response?.data?.detail ||
          "Could not add.",
      );
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (id) => {
    try {
      await removeFromWatchlist(id);
      setItems((cur) => cur.filter((x) => x.id !== id));
    } catch {
      setError("Could not remove that ticker.");
    }
  };

  return (
    <div className="p-6 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <EyeIcon className="w-5 h-5 text-indigo-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Watchlist
            </h3>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Tickers you're following but don't own. Lens is one click away.
          </p>
        </div>
        <span className="text-xs text-gray-400">{items.length} item{items.length === 1 ? "" : "s"}</span>
      </div>

      {/* Add form */}
      <form
        onSubmit={onAdd}
        className="flex flex-wrap gap-2 mb-4 p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/20"
      >
        <input
          value={draft.symbol}
          onChange={(e) =>
            setDraft({ ...draft, symbol: e.target.value.toUpperCase() })
          }
          placeholder="Symbol (e.g. NVDA)"
          maxLength={50}
          className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-bold tracking-wide"
        />
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Name (optional)"
          className="flex-1 min-w-[160px] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
        />
        <button
          type="submit"
          disabled={adding || !draft.symbol.trim()}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-semibold"
        >
          <PlusIcon className="w-4 h-4" />
          Add
        </button>
      </form>

      {error && (
        <p className="mb-3 text-xs text-red-500">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Empty. Add a ticker above to start following it.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-100 dark:bg-white/5"
            >
              <div className="min-w-0">
                <p className="font-bold tracking-wide text-gray-900 dark:text-white">
                  {item.symbol}
                </p>
                {item.name ? (
                  <p className="text-[11px] text-gray-500 truncate">
                    {item.name}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <AskCouncilButton
                  variant="icon"
                  prefill={`Tell me about ${item.symbol}${item.name ? ` (${item.name})` : ""}. I'm watching it but don't own any. Bull / bear case in 4 sentences.`}
                />
                <button
                  onClick={() => onRemove(item.id)}
                  className="w-7 h-7 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-500/10 flex items-center justify-center"
                  title="Remove from watchlist"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WatchlistCard;
